import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { Breadcrumb, BreadcrumbsService } from './breadcrumbs.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { LoginAsCompanyComponent } from '../loginPage/asCompany/login.as.company.component';
import { AdminV1 } from '../../interfaces/userLogin';
import { Subscription } from 'rxjs';
import { IdbCache } from '../../services/cache.etc/idb.cache';
import { AccountsDetailsComponent } from '../../../components/accounts/details/accounts.details.component';
import { ImageEditorComponent } from '../image-editor/image.editor.component';
import { SystemUpdateService } from '../../services/system-update.service';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.css'],
  standalone: false,
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  debug = false;
  isCollapsed = true;
  currentUserName = 'User';
  adminId: string;
  timerClose: ReturnType<typeof setTimeout> | undefined;
  timerOpen: ReturnType<typeof setTimeout> | undefined;
  timerMs = 200;
  discardDescriptor: false;
  adminMode: boolean;
  adminLoggedAsCompany: boolean;
  loggedCompanyDetails: AdminV1;

  ccCompany: Subscription;
  // Breadcrumbs >
  ccbc: Subscription;
  BC: Breadcrumb[] = [];
  bcAvailable: true | false | boolean = false;
  // Breadcrumbs /

  // User Guide Drawer
  guideAvailable = false;

  // System Updates
  msgCounter = 0;
  updatesModalVisible = false;
  private unseenUpdatesSub?: Subscription;

  // User Screen Scale
  currentZoom = 100;
  marks = {
    90: {
      style: {
        color: '#ac7305',
      },
      label: '<strong>90%</strong>',
    },
    100: {
      style: {
        color: '#0677b3',
      },
      label: '<strong>100%</strong>',
    },
    110: {
      style: {
        color: '#06b3a5',
      },
      label: '<strong>110%</strong>',
    },
    120: {
      style: {
        color: '#2d948b',
      },
      label: '<strong>120%</strong>',
    },
  };

  openImgEditor() {
    this.modalService.create({
      nzTitle: 'Image Editor',
      nzContent: ImageEditorComponent,
      nzWidth: 1400,
      nzCancelDisabled: true,
      nzOkText: 'close',
      nzData: {
        minImageWidth: 1200,
      },
    });
  }

  loginAsCompany() {
    this.modalService.create({
      nzTitle: 'Login as Company',
      nzContent: LoginAsCompanyComponent,
    });
  }

  initScale() {
    let imv: string = null;
    const uScale = decodeURIComponent(document.cookie)
      .toString()
      .split('; ')
      .filter((i) => i.indexOf('userScale') >= 0);
    if (uScale.length > 0) {
      imv = uScale[0].split('=')[1];
      this.currentZoom = +imv;
      this.scale(+imv);
    }
  }

  scale(e: number) {
    const d = new Date();
    const cname = 'userScale';
    d.setTime(d.getTime() + 86400000000);
    const expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + e + ';' + expires + ';path=/';
    setTimeout(() => {
      const html = document.getElementsByTagName('html')[0];
      const currentZoom = e / 100;
      const style = html.style as CSSStyleDeclaration & { zoom?: string };
      style.zoom = currentZoom.toString();
    }, 255);
  }

  zoom(i: number) {
    const html = document.getElementsByTagName('html')[0];
    const style = html.style as CSSStyleDeclaration & { zoom?: string };
    if (i > 0) {
      if (this.currentZoom <= 200) {
        this.currentZoom += 10;
        const currentZoom = (+style.zoom || 1) + 0.1;
        style.zoom = currentZoom.toString();
      }
    } else {
      if (this.currentZoom >= 80) {
        this.currentZoom -= 10;
        const currentZoom = (+style.zoom || 1) - 0.1;
        style.zoom = currentZoom.toString();
      }
    }
    const d = new Date();
    const cname = 'userScale';
    d.setTime(d.getTime() + 86400000000);
    const expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + this.currentZoom + ';' + expires + ';path=/';
  }

  // Menu UX
  openMenu() {
    clearTimeout(this.timerClose);
    this.timerOpen = setTimeout(() => {
      this.isCollapsed = false;
    }, this.timerMs);
  }

  closeMenu() {
    clearTimeout(this.timerOpen);
    if (!this.isCollapsed) {
      this.timerClose = setTimeout(() => {
        this.isCollapsed = true;
      }, this.timerMs);
    }
  }

  accountDetails() {
    console.log('OPEN USER', this.adminId);
    const modal = this.modalService.create({
      nzTitle: 'Account Details',
      nzContent: AccountsDetailsComponent,
      nzCancelDisabled: true,
      nzOkText: 'close',
      nzWidth: 1200,
      nzData: {
        uid: this.adminId,
      },
    });
    modal.componentInstance.sumbit.subscribe((res) => {
      modal.close();
    });
  }

  userLogOut() {
    this.auth.logout();
  }

  onUpdatesPopoverVisible(visible: boolean): void {
    this.systemUpdate.onUpdatesPopoverVisible(visible);
  }

  onUpdatesAcknowledged(): void {
    this.systemUpdate.markAsSeen();
    this.updatesModalVisible = false;
  }

  onUpdatesCancelled(): void {
    this.updatesModalVisible = false;
  }

  private initSystemUpdates(): void {
    let userId: string | number | null = null;
    try {
      userId = JSON.parse(localStorage.getItem('userData')!)?.adminId ?? null;
      if (userId != null) {
        this.systemUpdate.initForUser(userId);
      }
    } catch {
      // userData not ready yet
    }

    this.unseenUpdatesSub = this.systemUpdate.hasUnseenUpdates$.subscribe((unseen) => {
      this.msgCounter = unseen ? 1 : 0;
    });

    if (userId == null) {
      return;
    }

    setTimeout(() => {
      if (this.systemUpdate.consumeAutoModal(userId)) {
        this.updatesModalVisible = true;
      }
    }, 300);
  }

  constructor(
    private auth: AuthService,
    private title: Title,
    private meta: Meta,
    private router: Router,
    private breadcrumbs: BreadcrumbsService,
    private modalService: NzModalService,
    // DEBUG
    private idbc: IdbCache,
    public systemUpdate: SystemUpdateService,
  ) {
    // SEO Init
    title.setTitle('Tripium Inventory v2');
    meta.addTags([
      { name: 'keywords', content: 'Dashboard, Home, Main' },
      { name: 'Description', content: 'Tripium Inventory v2 - Main Page (Dashboard)' },
    ]);
    // Init Breadcrumbs
    this.ccbc = this.breadcrumbs.subscriptionOnStates().subscribe((cc) => {
      setTimeout(() => {
        this.BC = cc || [];
        this.bcAvailable = this.BC.length > 0;
      }, 0);
    });

    // init autoLogout
    auth.updateApiToken(null, true);
  }

  initAdminAsCompanyChanges() {
    this.ccCompany = this.auth.asCompanyChanges().subscribe(
      (data) => {
        if (data) {
          this.loggedCompanyDetails = data;
          this.adminLoggedAsCompany = true;
        } else {
          this.adminLoggedAsCompany = false;
        }
      },
      (e) => {
        this.adminLoggedAsCompany = false;
      },
    );
  }

  ngOnInit() {
    this.initScale();
    const CU = this.auth.getAuthenticatedUserData();
    this.currentUserName = CU?.administrator || 'User';
    this.adminId = CU?.adminId;
    this.adminMode = CU?.type === 'admin';
    this.initAdminAsCompanyChanges();
    this.initSystemUpdates();

    // DEBUG
    if (localStorage.getItem('DEBUG:editor')) {
      this.debug = true;
    }
  }

  ngOnDestroy() {
    if (this.ccbc) {
      this.ccbc.unsubscribe();
    }
    if (this.ccCompany) {
      this.ccCompany.unsubscribe();
    }
    this.unseenUpdatesSub?.unsubscribe();
  }
}
